import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { PenLine, ShieldCheck, Link2, Download, XCircle, Send, Search, Clock, Eye } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A' };
const STATUS = {
  sent: { c: C.blue, label: 'Sent', Icon: Send },
  viewed: { c: C.amber, label: 'Opened', Icon: Eye },
  signed: { c: C.green, label: 'Signed', Icon: ShieldCheck },
  expired: { c: C.slate, label: 'Expired', Icon: Clock },
  revoked: { c: C.red, label: 'Withdrawn', Icon: XCircle },
};
const KIND_HINT = {
  policy_schedule: 'Policy number, e.g. MOT-17953',
  claim_discharge: 'Claim reference, e.g. CLM-2026-485634',
  endorsement_acceptance: 'Endorsement number',
};

const KPI = ({ label, value, color }) => (
  <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.14)' }}>
    <div className="text-2xl font-bold font-mono" style={{ color: color || C.navy }}>{value}</div>
    <div className="text-[11px] uppercase tracking-wider mt-0.5" style={{ color: C.slate }}>{label}</div>
  </Card>
);

export default function SignaturesPage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ kind: 'policy_schedule', ref_id: '', signer_name: '', signer_email: '', message: '', send_email: true });
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState('');

  const load = useCallback(() => {
    api.get('/esign/requests?limit=200')
      .then(r => setData(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || 'Could not load the signature register'))
      .finally(() => setLoading(false));
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!form.ref_id.trim() || !form.signer_email.trim()) {
      toast.error('A reference and the signer’s email are both needed');
      return;
    }
    setSending(true);
    try {
      const r = await api.post('/esign/requests', form);
      const emailed = r.data?.delivery?.emailed;
      toast.success(emailed ? 'Sent to the signer' : 'Request created — the outbound guard held the email, copy the link instead');
      await navigator.clipboard?.writeText(r.data.link).catch(() => {});
      setForm({ ...form, ref_id: '', signer_name: '', signer_email: '', message: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'The request could not be created');
    }
    setSending(false);
  };

  const verify = async (id) => {
    setVerifying(id);
    try {
      const r = await api.get(`/esign/requests/${id}/verify`);
      const v = r.data;
      (v.verdict === 'intact' ? toast.success : toast.error)(v.detail, { duration: 8000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verification failed');
    }
    setVerifying('');
  };

  const revoke = async (id) => {
    try {
      await api.post(`/esign/requests/${id}/revoke`, { reason: 'Withdrawn from the register' });
      toast.success('Link withdrawn — it can no longer be opened');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not withdraw the link');
    }
  };

  const download = (id) => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/esign/requests/${id}/document`, '_blank');

  if (loading) return <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 rounded-md" />)}</div>;

  const rows = data?.requests || [];
  const counts = data?.counts || {};

  return (
    <div className="space-y-5" data-testid="signatures-page">
      <div>
        <h2 className="text-xl font-medium tracking-tight" style={{ color: C.navy }}>Signatures</h2>
        <p className="text-sm mt-0.5" style={{ color: C.slate }}>
          Send a schedule, an endorsement or a settlement discharge to be signed on screen. Each signature carries the
          signer’s typed name, consent, address and device, and a fingerprint of the signed document sealed into the audit trail.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI label="Awaiting signature" value={(counts.sent || 0) + (counts.viewed || 0)} color={C.blue} />
        <KPI label="Signed" value={counts.signed || 0} color={C.green} />
        <KPI label="Expired" value={counts.expired || 0} color={C.slate} />
        <KPI label="Withdrawn" value={counts.revoked || 0} color={C.red} />
        <KPI label="Signed rate" value={`${data?.signed_rate_pct ?? 0}%`} color={C.navy} />
      </div>

      <Card className="p-5 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.16)' }} data-testid="esign-new-request">
        <h3 className="text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-2" style={{ color: C.navy }}>
          <PenLine className="w-4 h-4" style={{ color: C.teal }} /> Send a document for signature
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <select value={form.kind} onChange={e => setForm({ ...form, kind: e.target.value })}
            data-testid="esign-kind-select"
            className="px-3 py-2 rounded-md text-sm" style={{ border: '1px solid rgba(136,150,168,0.3)', color: C.navy }}>
            {Object.entries(data?.kinds || {}).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5" style={{ color: C.slate }} />
            <input value={form.ref_id} onChange={e => setForm({ ...form, ref_id: e.target.value })}
              placeholder={KIND_HINT[form.kind]} data-testid="esign-ref-input"
              className="w-full pl-8 pr-3 py-2 rounded-md text-sm" style={{ border: '1px solid rgba(136,150,168,0.3)', color: C.navy }} />
          </div>
          <input value={form.signer_name} onChange={e => setForm({ ...form, signer_name: e.target.value })}
            placeholder="Signer name (defaults to the insured)" data-testid="esign-signer-name-input"
            className="px-3 py-2 rounded-md text-sm" style={{ border: '1px solid rgba(136,150,168,0.3)', color: C.navy }} />
          <input value={form.signer_email} onChange={e => setForm({ ...form, signer_email: e.target.value })}
            placeholder="Signer email" type="email" data-testid="esign-signer-email-input"
            className="px-3 py-2 rounded-md text-sm" style={{ border: '1px solid rgba(136,150,168,0.3)', color: C.navy }} />
          <Button onClick={send} disabled={sending} data-testid="esign-send-button"
            className="rounded-md text-white" style={{ background: C.blue }}>
            {sending ? 'Sending…' : 'Send for signature'}
          </Button>
        </div>
        <p className="text-[11px] mt-2" style={{ color: C.slate }}>
          The link is copied to your clipboard as well as emailed — if the outbound guard holds the email, you can still pass the link on.
        </p>
      </Card>

      <Card className="rounded-md overflow-hidden" style={{ border: '1px solid rgba(136,150,168,0.16)' }} data-testid="esign-register">
        {rows.length === 0 ? (
          <div className="p-10 text-center">
            <PenLine className="w-7 h-7 mx-auto mb-3" style={{ color: C.slate }} />
            <p className="text-sm font-medium" style={{ color: C.navy }}>Nothing sent for signature yet</p>
            <p className="text-xs mt-1" style={{ color: C.slate }}>Send a schedule above and it will appear here with its status.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr style={{ background: '#FAFBFC', color: C.slate }}>
                  {['Document', 'Signer', 'Status', 'Sent', 'Signed', ''].map(h => (
                    <th key={h} className="text-left font-semibold uppercase tracking-wider px-4 py-2.5">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const s = STATUS[r.status] || STATUS.sent;
                  return (
                    <tr key={r.request_id} style={{ borderTop: '1px solid rgba(136,150,168,0.1)' }} data-testid={`esign-row-${r.request_id}`}>
                      <td className="px-4 py-2.5">
                        <div className="font-medium" style={{ color: C.navy }}>{r.title}</div>
                        <div className="text-[11px]" style={{ color: C.slate }}>{data.kinds?.[r.kind] || r.kind}{r.business_name ? ` · ${r.business_name}` : ''}</div>
                      </td>
                      <td className="px-4 py-2.5">
                        <div style={{ color: C.navy }}>{r.signer_name}</div>
                        <div className="text-[11px]" style={{ color: C.slate }}>{r.signer_email}</div>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider"
                          style={{ background: `${s.c}18`, color: s.c }} data-testid={`esign-status-${r.request_id}`}>
                          <s.Icon className="w-3 h-3" /> {s.label}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 font-mono" style={{ color: C.slate }}>
                        {new Date(r.created_at).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' })}
                      </td>
                      <td className="px-4 py-2.5 font-mono" style={{ color: r.signed_at ? C.green : C.slate }}>
                        {r.signed_at ? new Date(r.signed_at).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' }) : '—'}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1.5 justify-end">
                          {r.status !== 'signed' && r.status !== 'revoked' && (
                            <>
                              <button title="Copy the signing link" data-testid={`esign-copy-${r.request_id}`}
                                onClick={() => { navigator.clipboard?.writeText(r.link); toast.success('Signing link copied'); }}
                                className="p-1.5 rounded hover:bg-slate-100"><Link2 className="w-3.5 h-3.5" style={{ color: C.blue }} /></button>
                              <button title="Withdraw the link" data-testid={`esign-revoke-${r.request_id}`}
                                onClick={() => revoke(r.request_id)}
                                className="p-1.5 rounded hover:bg-slate-100"><XCircle className="w-3.5 h-3.5" style={{ color: C.red }} /></button>
                            </>
                          )}
                          {r.status === 'signed' && (
                            <button title="Verify the signed document has not changed" data-testid={`esign-verify-${r.request_id}`}
                              onClick={() => verify(r.request_id)} disabled={verifying === r.request_id}
                              className="p-1.5 rounded hover:bg-slate-100"><ShieldCheck className="w-3.5 h-3.5" style={{ color: C.green }} /></button>
                          )}
                          <button title="Download the document" data-testid={`esign-download-${r.request_id}`}
                            onClick={() => download(r.request_id)}
                            className="p-1.5 rounded hover:bg-slate-100"><Download className="w-3.5 h-3.5" style={{ color: C.slate }} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="text-[11px]" style={{ color: C.slate }}>
        Electronic Transactions Act 1999 (Cth) s10 — consent, identification, approval and reliability. Identity here rests on
        control of the mailbox the one-time link was sent to plus a name matched to the document; it is not verified identity
        proofing, and the certificate page says so in those words.
      </p>
    </div>
  );
}
